import {
  filter,
  firstValueFrom,
  from,
  mergeMap,
  Observable,
  timeout,
} from 'rxjs';
import {
  componentTreeSelectionChanged,
  selectionStartSet,
} from '@ardoq/component-selection';
import { logError } from '@ardoq/logging';
import {
  action$,
  dispatchAction,
  extractPayload,
  ofType,
  withNamespace,
} from '@ardoq/rxbeach';
import {
  GetAdditionalRoutineArgs,
  NavigatorConfiguration,
  NavigatorLayoutState,
} from '../../types';
import {
  initialComponentsSelectedInTree,
  selectedComponentIdsOnOpeningDropdown,
} from '../../actions/navigatorActions';
import { isDisplayedAndTreeHasComponents } from '../../utils/isDisplayedAndTreeHasComponent';
import { nextDOMUpdate } from '@ardoq/common-helpers';
import { scrollNodeIntoViewIfNeeded } from '../../utils/scrollIntoViewUtils';
import {
  COMPONENTS_LOAD_TIMEOUT_MS,
  COMPONENT_MOUNT_TIMEOUT_MS,
} from '../../consts';

export const getHandleSelectedComponentsOnOpeningDropdownRoutine = ({
  navigator$,
  navigatorConfiguration,
}: GetAdditionalRoutineArgs) =>
  action$.pipe(
    withNamespace(navigatorConfiguration.actionNamespace),
    ofType(selectedComponentIdsOnOpeningDropdown),
    extractPayload(),
    mergeMap(componentIds =>
      from(
        selectAndExpandNodesInTree({
          navigator$,
          navigatorConfiguration,
          componentIds,
        })
      )
    )
  );

type SelectAndExpandNodesInTreeArgs = {
  navigator$: Observable<NavigatorLayoutState>;
  navigatorConfiguration: NavigatorConfiguration;
  componentIds: string[];
};

const selectAndExpandNodesInTree = async ({
  navigator$,
  navigatorConfiguration,
  componentIds,
}: SelectAndExpandNodesInTreeArgs) => {
  try {
    if (componentIds.length === 0) {
      return;
    }
    // Wait for the components to be available in the tree.
    const { container } = await firstValueFrom(
      navigator$.pipe(
        filter(isDisplayedAndTreeHasComponents(componentIds)),
        timeout(COMPONENTS_LOAD_TIMEOUT_MS)
      )
    );
    dispatchAction(
      componentTreeSelectionChanged({ treeSelection: componentIds }),
      navigatorConfiguration.actionNamespace
    );
    dispatchAction(
      selectionStartSet({ selectionStart: componentIds[0] }),
      navigatorConfiguration.actionNamespace
    );
    dispatchAction(
      initialComponentsSelectedInTree(componentIds),
      navigatorConfiguration.actionNamespace
    );
    await nextDOMUpdate(container);
    const updatedNavigatorState = await firstValueFrom(
      navigator$.pipe(
        filter(nodesAreExpanded(componentIds)),
        timeout(COMPONENT_MOUNT_TIMEOUT_MS)
      )
    );
    // This is not necessarily the first component in the flow of the tree,
    // but it's the best we can do without additional information for now.
    scrollNodeIntoViewIfNeeded(updatedNavigatorState, componentIds[0]);
  } catch (e) {
    logError(Error(`Components couldn't be selected and expanded in tree`));
  }
};

const nodesAreExpanded =
  (componentIds: string[]) =>
  ({ tree }: NavigatorLayoutState) =>
    componentIds.every(id => {
      const node = tree.getNode(id);
      return node?.isExpandedInTree() ?? false;
    });
